import { tourDaTela, type FaixaDeImpersonacao, type PassoDoTour } from '@levitas/contracts';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@levitas/ui';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useSessao } from '../../lib/usar-sessao';

interface Retangulo {
  top: number;
  left: number;
  width: number;
  height: number;
}

const FOLGA = 8;

/**
 * O tour da primeira visita de cada área (M18).
 *
 * Curto de propósito — três ou quatro passos. Tour longo é pulado, e tour
 * pulado não ensina ninguém.
 *
 * Ele roda **uma vez**, e o "já vi" fica na conta da pessoa: quem entra do
 * celular depois de ter visto no computador não vê de novo. E dá para repetir
 * quando quiser, pela central de ajuda.
 */
export function TourDaArea() {
  const { pathname } = useLocation();
  const { usuario, carregando, recarregar } = useSessao();
  const [passo, setPasso] = useState(0);
  const [dispensado, setDispensado] = useState(false);
  const [alvo, setAlvo] = useState<Retangulo | null>(null);

  /*
   * O suporte da El Shaday não vê o tour: a igreja não é dele, ele está ali
   * para olhar um problema, e o "já vi" nem poderia ser gravado — a sessão de
   * suporte é só de leitura (M17).
   */
  const { data: acessoDeSuporte } = useQuery({
    queryKey: ['acesso-de-suporte', usuario?.id ?? null],
    queryFn: () => api<FaixaDeImpersonacao | null>('/me/support-access'),
    retry: false,
  });

  const tour = tourDaTela(pathname);
  const jaViu = tour ? (usuario?.toursVistos ?? []).includes(tour.area) : true;
  /*
   * Enquanto a sessão carrega ainda não se sabe se a pessoa já viu — e mostrar
   * "por via das dúvidas" faria o tour piscar na cara de quem já conhece o
   * produto, em toda entrada de conexão lenta.
   */
  const rodando = !carregando && Boolean(tour) && !jaViu && !dispensado && !acessoDeSuporte;

  const atual: PassoDoTour | undefined = tour?.passos[passo];

  const encerrar = useCallback(async () => {
    setDispensado(true);
    if (!tour) return;
    try {
      await api(`/me/tours/${tour.area}`, { method: 'PUT' });
      await recarregar();
    } catch {
      // Não conseguiu marcar? O tour reaparece na próxima visita. Irritante,
      // mas melhor do que travar a tela por causa de um aviso.
    }
  }, [tour, recarregar]);

  /*
   * A posição é medida depois da pintura e refeita a cada passo: a tela pode
   * ter rolado, e um halo no lugar errado confunde mais do que ajuda.
   *
   * A medição é agendada para o quadro seguinte de propósito — medir no meio do
   * efeito mediria a tela antes de ela ter acabado de se acomodar, e ainda
   * dispararia uma renderização em cascata.
   */
  useLayoutEffect(() => {
    if (!rodando || !atual?.alvo) {
      const quadro = requestAnimationFrame(() => setAlvo(null));
      return () => cancelAnimationFrame(quadro);
    }

    const elemento = document.querySelector<HTMLElement>(`[data-testid="${atual.alvo}"]`);
    if (!elemento) {
      const quadro = requestAnimationFrame(() => setAlvo(null));
      return () => cancelAnimationFrame(quadro);
    }

    elemento.scrollIntoView({ block: 'center', behavior: 'smooth' });

    const medir = () => {
      const r = elemento.getBoundingClientRect();
      setAlvo({
        top: r.top - FOLGA,
        left: r.left - FOLGA,
        width: r.width + FOLGA * 2,
        height: r.height + FOLGA * 2,
      });
    };

    const quadro = requestAnimationFrame(medir);
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      cancelAnimationFrame(quadro);
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [rodando, atual]);

  // Esc encerra: quem já sabe usar não deve ficar preso ao tutorial.
  useEffect(() => {
    if (!rodando) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') void encerrar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [rodando, encerrar]);

  if (!rodando || !tour || !atual) return null;

  const ultimo = passo === tour.passos.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Tour: ${atual.titulo}`}
      data-testid="tour"
      className="fixed inset-0 z-50"
    >
      {/* O escurecido não intercepta o clique do alvo: o halo é para olhar. */}
      <div className="absolute inset-0 bg-black/60" aria-hidden />

      {alvo ? (
        <div
          aria-hidden
          data-testid="tour-halo"
          style={{ top: alvo.top, left: alvo.left, width: alvo.width, height: alvo.height }}
          className="pointer-events-none absolute rounded-[var(--radius-md)] shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] ring-2 ring-[var(--brand)]"
        />
      ) : null}

      <div
        className="absolute inset-x-4 bottom-24 mx-auto max-w-md rounded-xl bg-[var(--cor-superficie)] p-5 shadow-[var(--shadow-lg)] sm:bottom-10"
        data-testid="tour-cartao"
      >
        <p className="text-xs text-[var(--cor-texto-suave)]">
          Passo {passo + 1} de {tour.passos.length}
        </p>
        <h2 className="mt-1 text-base font-semibold">{atual.titulo}</h2>
        <p className="mt-1 text-sm text-[var(--cor-texto-suave)]">{atual.texto}</p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <Button variante="fantasma" onClick={() => void encerrar()} data-testid="pular-tour">
            Pular
          </Button>

          <div className="flex items-center gap-2">
            {passo > 0 ? (
              <Button variante="secundaria" onClick={() => setPasso((atual) => atual - 1)}>
                Voltar
              </Button>
            ) : null}

            <Button
              onClick={() => (ultimo ? void encerrar() : setPasso((atual) => atual + 1))}
              data-testid="proximo-do-tour"
            >
              {ultimo ? 'Entendi' : 'Próximo'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
